import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import type { CatalogApp } from '../api/queries';
import { AppLogo } from './AppLogo';

// Single-line cell with a hover tooltip for the full value (names, category).
function Ellipsis({ text }: { text: string }) {
  return (
    <Tooltip title={text} enterDelay={400}>
      <Box
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

const muted = (
  <Box component="span" sx={{ color: 'text.disabled' }}>
    —
  </Box>
);

// Shared column set for the Browse / My Apps list views. Pass `owned` (the set
// of app_names the user already has) to show a "you have this" check on Browse.
export function appColumns(owned?: Set<string>): GridColDef<CatalogApp>[] {
  return [
    {
      field: 'app_name',
      headerName: 'App',
      flex: 1.3,
      minWidth: 220,
      valueGetter: (_value, row) => row.display_name ?? row.app_name,
      renderCell: (params) => {
        const name = params.row.display_name ?? params.row.app_name;
        const have = owned?.has(params.row.app_name);
        return (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', height: '100%', minWidth: 0 }}
          >
            <AppLogo app={params.row} />
            <Ellipsis text={name} />
            {have && (
              <Tooltip title="You have access to this app">
                <CheckCircleRoundedIcon
                  fontSize="small"
                  sx={{ color: 'success.main', flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {params.row.requires_approval && (
              <Tooltip title="Access requires approval">
                <Chip size="small" color="warning" label="Approval" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 0.7,
      minWidth: 120,
      valueGetter: (value) => value ?? 'Uncategorized',
      renderCell: (params) => <Ellipsis text={String(params.value)} />,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 240,
      // Wraps to multiple lines — the grid uses auto row height so the full
      // description is readable instead of clipped.
      renderCell: (params) =>
        params.value ? (
          <Box
            sx={{
              whiteSpace: 'normal',
              lineHeight: 1.4,
              py: 0.75,
              wordBreak: 'break-word',
            }}
          >
            {String(params.value)}
          </Box>
        ) : (
          muted
        ),
    },
    {
      field: 'open',
      headerName: 'Launch',
      description: "Opens the app's login/homepage in a new tab. Shown only when a URL is on file.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const url = params.row.login_url || params.row.homepage_url;
        if (!url)
          return (
            <Tooltip title="No launch URL on file for this app">
              <span>{muted}</span>
            </Tooltip>
          );
        return (
          <Tooltip title="Open app">
            <IconButton
              size="small"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open ${params.row.app_name}`}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];
}
